import { useQuery } from "@tanstack/react-query";
import { StoreSetupRequest, StoreSetupResponse, StoreSetupView, type StoreSetupState } from "@townsy/contracts";
import { API_BASE, apiFetch, isApiError } from "./client";

/**
 * "Your store" on one screen (ADR-0020): read the owner's prefilled form with its one plain status, and save it with one
 * command. The server re-checks ownership, validates, filters text and audits; the browser never decides what goes live.
 */
const enc = encodeURIComponent;

export const storeSetupKeys = {
  all: ["store-setup"] as const,
  one: (propertyId: string) => ["store-setup", propertyId] as const,
};

export const fetchStoreSetup = (propertyId: string) => apiFetch(`${API_BASE}/stores/${enc(propertyId)}/setup`, { schema: StoreSetupView, cache: "no-cache" });

/** One Save. `idempotencyKey` is made once per press of Save (a retry of the same press reuses it). */
export const putStoreSetup = (propertyId: string, body: StoreSetupRequest, idempotencyKey: string) =>
  apiFetch(`${API_BASE}/stores/${enc(propertyId)}/setup`, {
    method: "PUT",
    body: StoreSetupRequest.parse(body),
    headers: { "idempotency-key": idempotencyKey },
    schema: StoreSetupResponse,
  });

export function newStoreSetupKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `store-${crypto.randomUUID()}`;
  return `store-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** How often the screen re-reads its status: quickly while the automated check runs, gently while a person decides. */
export function storeSetupPollMs(state: StoreSetupState | undefined): number | false {
  if (state === "checking") return 4000;
  if (state === "waiting") return 30_000;
  return false;
}

const noAuthRetry = (count: number, err: unknown) => !(isApiError(err) && [401, 403, 404, 422].includes(err.status)) && count < 2;

export function useStoreSetupQuery(propertyId: string | null) {
  return useQuery({
    queryKey: storeSetupKeys.one(propertyId ?? "none"),
    queryFn: () => fetchStoreSetup(propertyId as string),
    enabled: Boolean(propertyId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (q) => storeSetupPollMs(q.state.data?.status.state),
    retry: noAuthRetry,
  });
}
